import { useEffect, useRef } from 'react'
import type { ChatRoomMessageDto } from '@digital-twins/shared-types'
import {
  joinRoom,
  leaveRoom,
  onMessage,
  onTurnComplete,
} from '@/lib/services/chat-socket'

interface UseGuestChatRoomSocketOptions {
  roomToken: string | null
  onMessage: (message: ChatRoomMessageDto) => void
  onTurnComplete?: () => void
}

/**
 * Guest share chat rides the existing `/chat` namespace with a guest_session JWT.
 * HTTP stays the source of truth for history; this only invalidates on live events.
 */
export function useGuestChatRoomSocket({
  roomToken,
  onMessage: onIncoming,
  onTurnComplete: onComplete,
}: UseGuestChatRoomSocketOptions) {
  const onIncomingRef = useRef(onIncoming)
  onIncomingRef.current = onIncoming
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  useEffect(() => {
    if (!roomToken) {
      return
    }
    joinRoom(roomToken)
    const offMessage = onMessage((event) => {
      if (event.roomId !== roomToken) {
        return
      }
      onIncomingRef.current(event.message)
    })
    const offTurn = onTurnComplete((event) => {
      if (event.roomId !== roomToken) {
        return
      }
      onCompleteRef.current?.()
    })
    return () => {
      offMessage()
      offTurn()
      leaveRoom(roomToken)
    }
  }, [roomToken])
}
